import { Alert, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getConfigurations } from '../../../../modules/config';
import { createTestCaseExecutionsBulk, updateIteration } from '../../../../modules/executions';
import ConfirmationModal from '../../../../ui/primitives/ConfirmationModal';
import Modal from '../../../../ui/layout/Modal';
import ActionButton from '../../../../ui/primitives/ActionButton';
import NewIterationForm from './NewIterationForm';
import useDebounce from '../../../../hooks/useDebounce';
import { fetchAssociatedTestCases } from '../../../../modules/associations';

const PAGE_SIZE_ALL = 'all';

const CreateIterationModal = ({
  showModal,
  setShowModal,
  defaultStatus,
  testCycleId,
  fetchExecutionData,
  getIterationsByTestCycleId,
  projectId,
  noOfIterations,
  testCycleCountries,
  currentIterationData,
  mode,
  setMode,
  executions,
  onIterationEdited,
  fetchAllExecutionsForEdit,
}) => {
  const { addNotification } = useNotification();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState('discard');
  const [conflictData, setConflictData] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 400);
  const [selectedTestSuite, setSelectedTestSuite] = useState('');
  const [iterationName, setIterationName] = useState(`Iteration ${noOfIterations + 1}`);
  const [allLinkedTestCases, setAllLinkedTestCases] = useState([]);
  const [allExecutionsForEdit, setAllExecutionsForEdit] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  const resetModalState = useCallback(() => {
    setSelectedEnvironment('');
    setSelectedCountry('');
    setSelectedTestCases([]);
    setSearchValue('');
    setSelectedTestSuite('');
    setSelectAll(false);
    setIterationName(`Iteration ${noOfIterations + 1}`);
    setMode('create');
  }, [noOfIterations, setMode]);

  const handleClose = () => {
    setShowModal(false);
    resetModalState();
  };

  const fetchTestCases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchAssociatedTestCases(
        testCycleId,
        projectId,
        null,
        null,
        'Approved',
        null,
        1,
        PAGE_SIZE_ALL,
        true
      );
      setAllLinkedTestCases(response?.associations || []);
    } catch {
      addNotification('danger', 'Failed to fetch test cases.');
    } finally {
      setLoading(false);
    }
  }, [projectId, testCycleId, addNotification]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const envRes = await getConfigurations('environments', projectId);
      setEnvironments(envRes?.data || []);
    } catch {
      addNotification('danger', 'Failed to fetch configurations.');
    } finally {
      setLoading(false);
    }
  }, [projectId, addNotification]);

  useEffect(() => {
    if (showModal && testCycleId) {
      fetchData();
      if (mode === 'edit' && currentIterationData) {
        setIterationName(currentIterationData.name || '');
        setSelectedEnvironment(currentIterationData.environment || '');
        const currentCountry =
          currentIterationData.country?.length > 0
            ? currentIterationData.country[0]
            : '';
        setSelectedCountry(currentCountry);

        if (fetchAllExecutionsForEdit) {
          fetchAllExecutionsForEdit().then(data => setAllExecutionsForEdit(data));
        }
      } else {
        setIterationName(`Iteration ${noOfIterations + 1}`);
      }
    }
  }, [showModal, testCycleId, mode, currentIterationData, fetchData, noOfIterations, fetchAllExecutionsForEdit]);

  const testCases = useMemo(() => {
    let list = [...allLinkedTestCases];

    if (debouncedSearchQuery) {
      const q = debouncedSearchQuery.toLowerCase();
      list = list.filter(tc => tc?.testcase?.summary?.toLowerCase()?.includes(q));
    }
    if (selectedTestSuite && selectedTestSuite !== 'all') {
      list = list.filter(tc => tc?.testcase?.test_suites?.includes(selectedTestSuite));
    }

    return list;
  }, [allLinkedTestCases, debouncedSearchQuery, selectedTestSuite]);

  const noTestCases =
    allLinkedTestCases.length > 0 &&
    testCases.length === 0 &&
    !debouncedSearchQuery &&
    !selectedTestSuite &&
    !loading;

  const handleFormSubmit = async (forceCreate = false) => {
    setSubmitting(true);
    try {
      const payload = {
        execution: {
          releaseVersion: '',
          status: defaultStatus?.id,
          actualTime: null,
          testCycleId,
          environment: selectedEnvironment,
          executedBy: currentUser?.id,
          iterationName: iterationName.trim(),
          country: selectedCountry ? [selectedCountry] : null,
          forceCreate,
        },
        testCaseAssociations: selectedTestCases,
        projectId,
      };
      await createTestCaseExecutionsBulk(payload);
      addNotification('success', 'New iteration started successfully!');
      setShowConfirmationModal(false);
      handleClose();
      getIterationsByTestCycleId();
      fetchExecutionData(true);
    } catch (err) {
      const errorData = err?.data?.error || err?.error;
      if (errorData?.code === 'ITERATION_ENV_COUNTRY_CONFLICT' && !forceCreate) {
        setConflictData(errorData);
        setConfirmationMode('conflict');
        setShowConfirmationModal(true);
      } else {
        addNotification(
          'danger',
          err?.message || errorData?.message || 'Failed to start new iteration.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!iterationName.trim()) return;
    setSubmitting(true);
    try {
      const executionsToUpdate = selectedTestCases.filter(tc => tc.update);
      const payload = {
        iterationId: currentIterationData.id,
        iterationName,
        testCycleId: currentIterationData.test_cycle_id,
        testCaseAssociations: executionsToUpdate,
        projectId,
      };
      await updateIteration(payload);
      addNotification('success', 'Iteration updated successfully!');
      handleClose();
      onIterationEdited?.(currentIterationData.id);
      getIterationsByTestCycleId();
      fetchExecutionData(true);
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to update iteration.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    if (selectedEnvironment || selectedTestCases.length > 0) {
      setConfirmationMode('discard');
      setShowConfirmationModal(true);
    } else {
      handleClose();
    }
  };

  const handleDiscard = () => {
    setShowConfirmationModal(false);
    handleClose();
  };

  const isSubmitDisabled =
    loading ||
    !selectedEnvironment ||
    (mode === 'create' && selectedTestCases.length === 0) ||
    !iterationName.trim();

  return (
    <>
      <Modal
        open={showModal}
        handleClose={submitting ? undefined : handleCloseModal}
        width="md"
        modalTitle={mode === 'edit' ? 'Edit Iteration Details' : 'Start New Iteration'}
        className="create-iteration-modal"
        action={() => (
          <>
            <ActionButton
              label="Close"
              intent="secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            />
            <ActionButton
              label={submitting
                ? mode === 'edit' ? 'Saving…' : 'Starting…'
                : mode === 'edit' ? 'Save Changes' : 'Start'}
              intent="primary"
              onClick={() => (mode === 'edit' ? handleEditSubmit() : handleFormSubmit())}
              disabled={isSubmitDisabled || submitting}
              loading={submitting}
            />
          </>
        )}
      >
        {!noTestCases || mode === 'edit' ? (
          <NewIterationForm
            environments={environments}
            loading={loading}
            testCases={testCases}
            selectedEnvironment={selectedEnvironment}
            setSelectedEnvironment={setSelectedEnvironment}
            selectedTestCases={selectedTestCases}
            setSelectedTestCases={setSelectedTestCases}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            selectedTestSuite={selectedTestSuite}
            setSelectedTestSuite={setSelectedTestSuite}
            testCycleId={testCycleId}
            iterationName={iterationName}
            setIterationName={setIterationName}
            allLinkedTestCases={allLinkedTestCases}
            testCycleCountries={testCycleCountries}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            mode={mode}
            executions={mode === 'edit' ? allExecutionsForEdit : executions}
            fetchTestCases={fetchTestCases}
          />
        ) : (
          <Alert severity="warning">
            No approved test cases are available for execution. Please add approved test cases
            to the test cycle.
          </Alert>
        )}
      </Modal>

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={() => {
          if (confirmationMode === 'conflict') {
            setShowConfirmationModal(false);
            handleFormSubmit(true);
          } else {
            handleDiscard();
          }
        }}
        title={confirmationMode === 'conflict' ? 'Confirmation Warning' : 'Discard'}
        body={
          confirmationMode === 'conflict' ? (
            <Typography>
              Test executions already exist for this environment and country.
              {conflictData?.non_editable_count && (
                <>
                  {' '}If you continue,{' '}
                  <strong>{conflictData.non_editable_count}</strong>{' '}
                  test case{conflictData.non_editable_count > 1 ? 's' : ''} will become
                  uneditable in an existing iteration.
                </>
              )}
            </Typography>
          ) : (
            'Are you sure you want to discard changes?'
          )
        }
        buttonLabel={confirmationMode === 'conflict' ? 'Create Anyway' : 'Discard'}
        buttonLabelSecondary={confirmationMode === 'conflict' ? 'Cancel' : 'Go Back'}
        buttonStyle="primary"
        loading={submitting}
      />
    </>
  );
};

export default CreateIterationModal;